<script setup>
import { computed, ref, onMounted, watch, nextTick } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Evolucao',
  },
  subtitle: {
    type: String,
    default: '',
  },
});

const chartContainer = ref(null);
const tooltip = ref({ show: false, x: 0, y: 0, date: '', value: 0 });

// Chart dimensions
const chartWidth = 800;
const chartHeight = 250;
const padding = { top: 20, right: 20, bottom: 40, left: 40 };

// Helper to safely extract numeric value
const safeNumber = val => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};

// Process data for chart
const chartData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return props.data
    .map(d => {
      let date;
      if (d.timestamp) {
        date = new Date(d.timestamp * 1000);
      } else if (d.date) {
        date = new Date(d.date);
      } else if (d.label) {
        date = new Date(d.label);
      } else {
        date = new Date();
      }
      // Validate date
      if (isNaN(date.getTime())) {
        date = new Date();
      }
      return {
        date,
        value: safeNumber(d.value ?? d.count ?? 0),
      };
    })
    .sort((a, b) => a.date - b.date);
});

// Calculate scales
const xScale = computed(() => {
  if (chartData.value.length === 0) return () => padding.left;
  const dates = chartData.value.map(d => d.date.getTime());
  const min = Math.min(...dates);
  const max = Math.max(...dates);
  const range = max - min || 1;
  return date => {
    const x =
      padding.left +
      ((date.getTime() - min) / range) *
        (chartWidth - padding.left - padding.right);
    return Number.isFinite(x) ? x : padding.left;
  };
});

const yScale = computed(() => {
  const defaultY = chartHeight - padding.bottom;
  if (chartData.value.length === 0) return () => defaultY;
  const values = chartData.value.map(d => d.value);
  const max = Math.max(...values, 1);
  return value => {
    const safeValue = safeNumber(value);
    const y =
      chartHeight -
      padding.bottom -
      (safeValue / max) * (chartHeight - padding.top - padding.bottom);
    return Number.isFinite(y) ? y : defaultY;
  };
});

// Generate path for line chart
const linePath = computed(() => {
  if (chartData.value.length === 0) return '';
  return chartData.value
    .map((d, i) => {
      const x = xScale.value(d.date);
      const y = yScale.value(d.value);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
});

// Generate area path
const areaPath = computed(() => {
  if (chartData.value.length === 0) return '';
  const line = chartData.value
    .map((d, i) => {
      const x = xScale.value(d.date);
      const y = yScale.value(d.value);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  const lastX = xScale.value(chartData.value[chartData.value.length - 1].date);
  const firstX = xScale.value(chartData.value[0].date);
  const bottomY = chartHeight - padding.bottom;

  return `${line} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
});

// Y-axis ticks
const yTicks = computed(() => {
  if (chartData.value.length === 0) return [];
  const values = chartData.value.map(d => d.value);
  const max = Math.max(...values, 1);
  const ticks = [];
  const step = Math.ceil(max / 5);
  for (let i = 0; i <= max; i += step) {
    ticks.push({
      value: i,
      y: yScale.value(i),
    });
  }
  return ticks;
});

// X-axis labels
const xLabels = computed(() => {
  if (chartData.value.length === 0) return [];
  const step = Math.ceil(chartData.value.length / 10);
  return chartData.value
    .filter((_, i) => i % step === 0)
    .map(d => ({
      date: d.date,
      label: `${String(d.date.getDate()).padStart(2, '0')}/${String(d.date.getMonth() + 1).padStart(2, '0')}`,
      x: xScale.value(d.date),
    }));
});

// Tooltip handlers
function showTooltip(event, data) {
  const rect = chartContainer.value?.getBoundingClientRect();
  if (!rect) return;

  tooltip.value = {
    show: true,
    x: event.clientX - rect.left,
    y: event.clientY - rect.top - 10,
    date: `${String(data.date.getDate()).padStart(2, '0')}/${String(data.date.getMonth() + 1).padStart(2, '0')}`,
    value: data.value,
  };
}

function hideTooltip() {
  tooltip.value.show = false;
}
</script>

<template>
  <div
    ref="chartContainer"
    class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
  >
    <!-- Header -->
    <div class="mb-4">
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      <p v-if="subtitle" class="text-sm text-n-slate-11">{{ subtitle }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="chartData.length === 0"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponíveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="relative">
      <svg
        :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
        class="w-full h-64"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#3b82f6" stop-opacity="0.3" />
            <stop offset="100%" stop-color="#3b82f6" stop-opacity="0" />
          </linearGradient>
        </defs>

        <!-- Grid lines -->
        <g class="grid-lines">
          <line
            v-for="tick in yTicks"
            :key="tick.value"
            :x1="padding.left"
            :y1="tick.y"
            :x2="chartWidth - padding.right"
            :y2="tick.y"
            stroke="currentColor"
            stroke-opacity="0.1"
            stroke-dasharray="4"
          />
        </g>

        <!-- Y-axis labels -->
        <g class="y-axis">
          <text
            v-for="tick in yTicks"
            :key="tick.value"
            :x="padding.left - 8"
            :y="tick.y + 4"
            text-anchor="end"
            class="fill-n-slate-10 text-xs"
          >
            {{ tick.value }}
          </text>
        </g>

        <!-- X-axis labels -->
        <g class="x-axis">
          <text
            v-for="label in xLabels"
            :key="label.x"
            :x="label.x"
            :y="chartHeight - 10"
            text-anchor="middle"
            class="fill-n-slate-10 text-xs"
          >
            {{ label.label }}
          </text>
        </g>

        <!-- Area -->
        <path :d="areaPath" fill="url(#areaGradient)" />

        <!-- Line -->
        <path
          :d="linePath"
          fill="none"
          stroke="#3b82f6"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <!-- Data points (hover targets) -->
        <g class="data-points">
          <circle
            v-for="(d, i) in chartData"
            :key="i"
            :cx="xScale(d.date)"
            :cy="yScale(d.value)"
            r="12"
            fill="transparent"
            class="cursor-pointer"
            @mouseenter="showTooltip($event, d)"
            @mouseleave="hideTooltip"
          />
        </g>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="tooltip.show"
        class="absolute pointer-events-none px-2 py-1 rounded-md bg-n-solid-3 border border-n-weak shadow-lg text-xs"
        :style="{
          left: `${tooltip.x}px`,
          top: `${tooltip.y}px`,
          transform: 'translate(-50%, -100%)',
        }"
      >
        <div class="font-medium text-n-slate-12">
          {{ tooltip.value }} conversas
        </div>
        <div class="text-n-slate-11">{{ tooltip.date }}</div>
      </div>
    </div>
  </div>
</template>
